import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import MetricsGrid from './MetricsGrid'
import CommutersMetrics from './CommutersMetrics'
import {
  DashboardCompanyTaxSavingsByPeriod,
  DashboardPeriod,
  DashboardTracker,
} from '../dashboardTypes'
import { ChangeVariant } from './TitleChange'

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

interface CommutersTopGridProps {
  trackers: DashboardTracker | null
  period: DashboardPeriod
  taxSavings?: DashboardCompanyTaxSavingsByPeriod | null
}

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  7: 'previous 7d',
  30: 'previous 30d',
  90: 'previous 90d',
  all: 'all time',
}

const percentChange = (current: number, previous: number) => {
  if (current === 0 && previous === 0) {
    return 0
  }
  if (previous === 0) {
    return current === 0 ? 0 : 100
  }
  return Math.round(((current - previous) / previous) * 100)
}

const changeVariant = (value: number): ChangeVariant => {
  if (value > 0) {
    return 'positive'
  }
  if (value < 0) {
    return 'negative'
  }
  return 'neutral'
}

const formatMoney = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`

const participationRate = (activeCommuters: number, totalCommuters: number) => {
  if (!totalCommuters || !activeCommuters) {
    return 0
  }
  return Math.round(
    (Math.min(activeCommuters, totalCommuters) / totalCommuters) * 100
  )
}

const CommutersTopGrid = ({
  trackers,
  period,
  taxSavings = null,
}: CommutersTopGridProps) => {
  const stats = trackers?.[period]
  const periodLabel = PERIOD_LABELS[period]
  const showLoading = !stats

  const totalCurrent = stats?.totalCommuters.current ?? 0
  const totalPrevious = stats?.totalCommuters.previous ?? 0
  const activeCurrent = stats?.activeCommuters.current ?? 0
  const activePrevious = stats?.activeCommuters.previous ?? 0

  const totalChange = percentChange(totalCurrent, totalPrevious)
  const activeChange = percentChange(activeCurrent, activePrevious)
  const participationChange = percentChange(
    participationRate(activeCurrent, totalCurrent),
    participationRate(activePrevious, totalPrevious)
  )

  const changeLabel = (value: number) =>
    period === 'all' ? undefined : `${value}% from ${periodLabel}`

  const periodTax = taxSavings?.[period]
  const taxCurrent =
    (periodTax?.employeeTaxSavings.current ?? 0) +
    (periodTax?.employerTaxSavings.current ?? 0)
  const taxPrevious =
    (periodTax?.employeeTaxSavings.previous ?? 0) +
    (periodTax?.employerTaxSavings.previous ?? 0)
  const taxChange = percentChange(taxCurrent, taxPrevious)

  return (
    <>
      <Header>
        <Text variant='h3' fontWeight={500}>
          At a Glance
        </Text>
      </Header>
      <MetricsGrid
        spacing={2}
        marginBottom={3}
        noOfColumns={4}
        columns={[
        <div data-cy='commuters-stat-eligible'>
          <CommutersMetrics
            title={`${totalCurrent}`}
            titleFontWeight={600}
            titleVariant='h2'
            subtitle='Eligible Commuters'
            change={changeLabel(totalChange)}
            isLoading={showLoading}
            variant={changeVariant(totalChange)}
          />
        </div>,
        <div data-cy='commuters-stat-enrolled'>
          <CommutersMetrics
            title={`${activeCurrent}`}
            titleFontWeight={600}
            titleVariant='h2'
            subtitle='Enrolled Commuters'
            change={changeLabel(activeChange)}
            isLoading={showLoading}
            variant={changeVariant(activeChange)}
          />
        </div>,
        <div data-cy='commuters-stat-participation'>
          <CommutersMetrics
            title={`${participationRate(activeCurrent, totalCurrent)}%`}
            titleFontWeight={600}
            titleVariant='h2'
            subtitle='Participation Rate'
            change={changeLabel(participationChange)}
            isLoading={showLoading}
            variant={changeVariant(participationChange)}
          />
        </div>,
        <div data-cy='commuters-stat-tax-savings'>
          <CommutersMetrics
            title={formatMoney(taxCurrent)}
            titleFontWeight={600}
            titleVariant='h2'
            subtitle='Tax Savings'
            change={changeLabel(taxChange)}
            isLoading={!periodTax}
            variant={changeVariant(taxChange)}
          />
        </div>,
      ]}
      />
    </>
  )
}

export default React.memo(CommutersTopGrid)
